import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { captureError } from "@/lib/sentry";

export interface AIRiskAssessment {
  riskScore: number; // 0-100
  riskLevel: "low" | "medium" | "high" | "critical";
  explanation: string;
  concerns: string[];
  recommendations: string[];
  similarDeals?: {
    id: string;
    title: string;
    outcome: string;
  }[];
}

export async function analyzeDealWithAI(deal: {
  title: string;
  description?: string;
  brand_name: string;
  amount: number;
  deliverables?: string;
  deal_type: string;
}): Promise<AIRiskAssessment> {
  try {
    const { data, error } = await supabase.functions.invoke('analyze-deal', {
      body: { deal }
    });

    if (error) {
      handleAIServiceError(error, 'Deal Analysis');
      throw error;
    }

    return data;
  } catch (error) {
    captureError(
      error instanceof Error ? error : new Error('AI deal analysis failed'),
      {
        service: 'analyzeDealWithAI',
        dealTitle: deal.title,
        dealType: deal.deal_type,
      }
    );
    throw error;
  }
}

export async function askAIQuestion(question: string, context?: any): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke('ai-chat', {
      body: { question, context }
    });

    if (error) {
      handleAIServiceError(error, 'AI Chat');
      throw error;
    }

    return data.answer;
  } catch (error) {
    captureError(
      error instanceof Error ? error : new Error('AI chat failed'),
      {
        service: 'askAIQuestion',
        questionLength: question.length,
        hasContext: !!context,
      }
    );
    throw error;
  }
}

// Helper function for user-friendly error messages
function handleAIServiceError(error: any, feature: string) {
  const errorMessage = error?.message || '';
  
  // Rate limit error (429)
  if (errorMessage.includes('Rate limit exceeded') || error?.status === 429) {
    toast({
      title: `${feature} Rate Limit Reached`,
      description: 'You can make up to 20 AI queries per hour. Please try again in a few minutes.',
      variant: 'destructive',
      duration: 6000,
    });
    return;
  }
  
  // Authentication error (401)
  if (errorMessage.includes('Unauthorized') || error?.status === 401) {
    toast({
      title: 'Session Expired',
      description: 'Please sign in again to continue using AI features.',
      variant: 'destructive',
    });
    return;
  }
  
  // Network/timeout error
  if (errorMessage.includes('Failed to fetch') || errorMessage.includes('Network')) {
    toast({
      title: `${feature} Unavailable`,
      description: 'Unable to connect to AI service. Please check your connection and try again.',
      variant: 'destructive',
    });
    return;
  }
  
  // Generic error
  toast({
    title: `${feature} Error`,
    description: 'An unexpected error occurred. Our team has been notified.',
    variant: 'destructive',
  });
}
